import Swal from 'sweetalert2';

export interface ConfirmOptions {
  title?: string;
  text?: string;
  confirmText?: string;
  cancelText?: string;
  icon?: 'warning' | 'error' | 'success' | 'info' | 'question';
  isDestructive?: boolean;
}

const customSwal = Swal.mixin({
  customClass: {
    popup: 'trainova-swal-popup',
    title: 'trainova-swal-title',
    htmlContainer: 'trainova-swal-html',
    confirmButton: 'trainova-swal-confirm',
    cancelButton: 'trainova-swal-cancel',
  },
  buttonsStyling: false,
});

/**
 * Modern themed confirmation dialog replacing browser window.confirm
 */
export async function showConfirm({
  title = 'Are you sure?',
  text = '',
  confirmText = 'Yes, Proceed',
  cancelText = 'Cancel',
  icon = 'warning',
  isDestructive = false,
}: ConfirmOptions): Promise<boolean> {
  const result = await customSwal.fire({
    title,
    text,
    icon,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    reverseButtons: true,
    focusCancel: true,
    customClass: {
      popup: 'trainova-swal-popup',
      title: 'trainova-swal-title',
      htmlContainer: 'trainova-swal-html',
      confirmButton: isDestructive ? 'trainova-swal-confirm--destructive' : 'trainova-swal-confirm',
      cancelButton: 'trainova-swal-cancel',
    },
  });

  return result.isConfirmed;
}

/**
 * Modern themed success alert
 */
export async function showSuccess(title: string, text: string = ''): Promise<void> {
  await customSwal.fire({
    title,
    text,
    icon: 'success',
    confirmButtonText: 'OK',
  });
}

/**
 * Modern themed error alert
 */
export async function showError(title: string, text: string = ''): Promise<void> {
  await customSwal.fire({
    title,
    text,
    icon: 'error',
    confirmButtonText: 'OK',
  });
}

/**
 * Modern themed warning alert
 */
export async function showWarning(title: string, text: string = ''): Promise<void> {
  await customSwal.fire({
    title,
    text,
    icon: 'warning',
    confirmButtonText: 'Understood',
  });
}

/**
 * Modern themed info alert
 */
export async function showInfo(title: string, text: string = ''): Promise<void> {
  await customSwal.fire({
    title,
    text,
    icon: 'info',
    confirmButtonText: 'Got it',
  });
}

/**
 * Modern non-intrusive toast notification
 */
export function showToast(title: string, icon: 'success' | 'error' | 'warning' | 'info' = 'success'): void {
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
  });

  Toast.fire({
    icon,
    title,
  });
}

export default {
  confirm: showConfirm,
  success: showSuccess,
  error: showError,
  warning: showWarning,
  info: showInfo,
  toast: showToast,
};
